// src/utils/controleImportFnct.ts — SCE Issues
// Le rapport de contrôle de l'import de la base FNC-T, la vérification
// aller-retour et le journal (lot 8 du § 14, méthode validée le 22/10). Pur,
// testable hors ligne.
//
// Principe : on corrige dans l'EXCEL, pas dans le code. L'import ne corrige que
// la forme (fnctImport) ; le fond se répare à la source tant que l'Excel fait
// foi. Le rapport classe chaque anomalie :
//  - bloquant : rien n'est écrit tant qu'elle existe (N° en double) ;
//  - à corriger : une case de l'Excel à reprendre (liste téléchargeable, N° et colonne) ;
//  - signalé : importé tel quel, dit dans le rapport et le journal.
// La base SharePoint ne prend que les FNC-T depuis le 01/01/2024 (décision du 22/10) :
// les plus anciennes et les actions orphelines de 2023 sont oubliées.
// Après l'écriture, SharePoint est relu et comparé champ par champ : aucun écart toléré.

import type { ActionFnct, Fnct, Jour } from '@/types/fnct.types'
import { CATEGORIES_FNCT, LIEUX_FNCT, PRIORITES_FNCT } from '@/config/fnct.config'
import { COLONNES_ACTIONS, COLONNES_FNCT, ecarts } from './fnctColonnes'
import type { BaseFnctLue } from './fnctImport'

export const ANNEE_DEBUT_IMPORT = 2024

export type NiveauAnomalie = 'bloquant' | 'corriger' | 'signale'

export interface Anomalie {
  niveau: NiveauAnomalie
  /** N° de FNC-T ou ID d'action ; « — » pour la base entière. */
  ref: string
  /** La colonne de l'Excel à regarder. */
  colonne: string
  valeur: string
  message: string
}

/** Les FNC-T depuis le 01/01/2024 et leurs actions ; ce qui est laissé de côté est compté. */
export function depuisDebutImport<T extends Pick<BaseFnctLue, 'fncts' | 'actions'>>(base: T): { base: T; fnctsAvant: number; actionsAvant: number } {
  const fncts = base.fncts.filter((f) => f.annee >= ANNEE_DEBUT_IMPORT)
  const gardees = new Set(fncts.map((f) => f.numero.toUpperCase()))
  const actions = base.actions.filter((a) => gardees.has(a.fnct.toUpperCase()))
  return { base: { ...base, fncts, actions }, fnctsAvant: base.fncts.length - fncts.length, actionsAvant: base.actions.length - actions.length }
}

const CATEGORIES = new Set(CATEGORIES_FNCT.flatMap((g) => g.categories.map((c) => c.valeur)))
const LIEUX = new Set(LIEUX_FNCT.map((l) => l.valeur))
const PRIORITES = new Set(PRIORITES_FNCT.map((p) => p.valeur))
const CRANS = new Set([2, 5, 7, 10])
const FORMAT_SERIE = /^\d{4}-(H|S|SC|B|SD|CO|G)$/i

const jj = (j: Jour | null | undefined): string => (j ? `${j.slice(8, 10)}/${j.slice(5, 7)}/${j.slice(0, 4)}` : '')

/** Le rapport de contrôle, du plus grave au moins grave, puis par N°. */
export function controlerBaseFnct(
  base: Pick<BaseFnctLue, 'fncts' | 'actions' | 'orphelines' | 'lignesSansNumero'>,
  aujourdhui: Jour,
): Anomalie[] {
  const res: Anomalie[] = []
  const a = (niveau: NiveauAnomalie, ref: string, colonne: string, valeur: string, message: string): void => {
    res.push({ niveau, ref, colonne, valeur, message })
  }

  // ─── Bloquant ───
  const vus = new Map<string, number>()
  for (const f of base.fncts) vus.set(f.numero.toUpperCase(), (vus.get(f.numero.toUpperCase()) ?? 0) + 1)
  for (const [n, fois] of vus) if (fois > 1) a('bloquant', n, 'NC #', n, `N° présent ${fois} fois dans l’Excel : une seule ligne par FNC-T`)

  // ─── À corriger dans l'Excel ───
  if (base.lignesSansNumero > 0)
    a('corriger', '—', 'NC #', '', `${base.lignesSansNumero} ligne${base.lignesSansNumero > 1 ? 's' : ''} remplie${base.lignesSansNumero > 1 ? 's' : ''} sans N° lisible : non importée${base.lignesSansNumero > 1 ? 's' : ''}`)
  const parFnct = new Map<string, ActionFnct[]>()
  for (const x of base.actions) parFnct.set(x.fnct.toUpperCase(), [...(parFnct.get(x.fnct.toUpperCase()) ?? []), x])
  for (const f of base.fncts) {
    const siennes = parFnct.get(f.numero.toUpperCase()) ?? []
    if (!f.description.trim()) a('corriger', f.numero, 'NonConformity description', '', 'Description vide : elle fait le titre de la fiche et du rapport client')
    if (f.cloture) {
      const ouvertes = siennes.filter((x) => !x.cloture)
      if (ouvertes.length) a('corriger', f.numero, 'Closing date (Coordinator)', jj(f.cloture), `Close alors que ${ouvertes.map((x) => x.id.slice(-3)).join(', ')} ${ouvertes.length > 1 ? 'ne sont pas closes' : 'n’est pas close'}`)
      if (f.enregistrement && f.cloture < f.enregistrement) a('corriger', f.numero, 'Closing date (Coordinator)', jj(f.cloture), `Close avant son enregistrement (${jj(f.enregistrement)})`)
    }
    for (const [colonne, d] of [['Recording date', f.enregistrement], ['Detection date', f.dateDetection], ['Closing date (Coordinator)', f.cloture]] as const)
      if (d && d > aujourdhui) a('corriger', f.numero, colonne, jj(d), 'Date dans le futur')
    if (f.categorie && !CATEGORIES.has(f.categorie)) a('corriger', f.numero, 'Problem category', f.categorie, 'Catégorie inconnue de l’appli')
    if (f.lieuDetection && !LIEUX.has(f.lieuDetection)) a('corriger', f.numero, 'Place of detection', f.lieuDetection, 'Lieu de détection inconnu de l’appli')
    if (f.priorite && !PRIORITES.has(f.priorite)) a('corriger', f.numero, 'Priority', f.priorite, 'Priorité inconnue (Low, Medium, High)')
    for (const [colonne, v] of [['Severity', f.severite], ['Frequency', f.frequence], ['Detectability', f.detectabilite]] as const)
      if (v !== null && !CRANS.has(v)) a('corriger', f.numero, colonne, String(v), 'Note hors des valeurs 2, 5, 7, 10')
  }
  for (const x of base.actions) {
    if (!x.responsable.trim()) a('corriger', x.id, 'Action Resp.', '', 'Action sans responsable')
    if (!x.categorie) a('corriger', x.id, 'Action cat.', '', 'Catégorie d’action inconnue (CO, RCA, CA, PA, OA)')
    if (x.cloture && x.ouverture && x.cloture < x.ouverture) a('corriger', x.id, 'Closing date', jj(x.cloture), `Close avant son ouverture (${jj(x.ouverture)})`)
    if (x.echeanceReportee && x.echeance && x.echeanceReportee < x.echeance) a('corriger', x.id, 'Renewed due date', jj(x.echeanceReportee), `Reportée avant l’échéance initiale (${jj(x.echeance)})`)
    if (x.cloture && x.cloture > aujourdhui) a('corriger', x.id, 'Closing date', jj(x.cloture), 'Date dans le futur')
  }

  // ─── Signalé : importé tel quel ───
  for (const f of base.fncts) {
    if (f.dateDetection && f.enregistrement && f.dateDetection > f.enregistrement)
      a('signale', f.numero, 'Detection date', jj(f.dateDetection), `Détectée après son enregistrement (${jj(f.enregistrement)})`)
    if (f.cloture && !f.legitimite) a('signale', f.numero, 'NC Legitimacy', '', 'Close sans légitimité')
    for (const ns of f.numerosSerie) if (!FORMAT_SERIE.test(ns)) a('signale', f.numero, 'Serial Number', ns, 'N° de série inhabituel (gardé tel quel)')
    if ((parFnct.get(f.numero.toUpperCase()) ?? []).length === 0) a('signale', f.numero, 'NC #', '', 'Aucune action')
  }
  for (const o of base.orphelines) a('signale', o.numero, 'NC #', o.numero, 'Action sans FNC-T dans la base : oubliée (décision du 22/10)')

  const rang: Record<NiveauAnomalie, number> = { bloquant: 0, corriger: 1, signale: 2 }
  return res.sort((x, y) => rang[x.niveau] - rang[y.niveau] || x.ref.localeCompare(y.ref, 'fr', { numeric: true }))
}

export function compterAnomalies(l: readonly Anomalie[]): Record<NiveauAnomalie, number> {
  return {
    bloquant: l.filter((x) => x.niveau === 'bloquant').length,
    corriger: l.filter((x) => x.niveau === 'corriger').length,
    signale: l.filter((x) => x.niveau === 'signale').length,
  }
}

const LIBELLES_NIVEAU: Record<NiveauAnomalie, string> = { bloquant: 'Bloquant', corriger: 'À corriger dans l’Excel', signale: 'Signalé' }

/** La liste à reprendre dans l'Excel : CSV `;` avec BOM (s'ouvre tel quel dans Excel). */
export function anomaliesEnCsv(l: readonly Anomalie[]): string {
  const cellule = (v: string): string => (/[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)
  const lignes = [['Niveau', 'N°', 'Colonne', 'Valeur', 'Ce qui ne va pas'], ...l.map((x) => [LIBELLES_NIVEAU[x.niveau], x.ref, x.colonne, x.valeur, x.message])]
  return `﻿${lignes.map((r) => r.map(cellule).join(';')).join('\r\n')}\r\n`
}

// ─── Vérification aller-retour ───

export interface EcartAllerRetour {
  ref: string
  /** « Absente de SharePoint », ou la colonne qui diffère. */
  colonne: string
  attendu: string
  relu: string
}

/**
 * Ce qui a été importé, comparé à ce que SharePoint porte une fois relu, champ
 * par champ (mêmes règles que l'aperçu : une adresse vide côté Excel ne compte
 * pas). Les valeurs calculées (statut d'action, RPN, criticité) en découlent :
 * mêmes champs, mêmes règles, mêmes résultats. Vide = aucun écart.
 */
export function comparerAllerRetour(
  importe: { fncts: readonly Fnct[]; actions: readonly ActionFnct[] },
  relu: { fncts: readonly Fnct[]; actions: readonly ActionFnct[] },
): EcartAllerRetour[] {
  const res: EcartAllerRetour[] = []
  const fncts = new Map(relu.fncts.map((f) => [f.numero.toUpperCase(), f]))
  const actions = new Map(relu.actions.map((x) => [x.id.toUpperCase(), x]))
  for (const f of importe.fncts) {
    const r = fncts.get(f.numero.toUpperCase())
    if (!r) res.push({ ref: f.numero, colonne: 'Absente de SharePoint', attendu: f.numero, relu: '' })
    else for (const e of ecarts(f, r, COLONNES_FNCT)) res.push({ ref: f.numero, colonne: e.libelle, attendu: e.apres, relu: e.avant })
  }
  for (const x of importe.actions) {
    const r = actions.get(x.id.toUpperCase())
    if (!r) res.push({ ref: x.id, colonne: 'Absente de SharePoint', attendu: x.id, relu: '' })
    else for (const e of ecarts(x, r, COLONNES_ACTIONS)) res.push({ ref: x.id, colonne: e.libelle, attendu: e.apres, relu: e.avant })
  }
  return res
}

// ─── Le journal ───

export interface JournalImportFnct {
  v: 1
  le: string
  par: string
  fichier: string
  essai: boolean
  lues: { fncts: number; actions: number }
  laisseesAvant2024: { fncts: number; actions: number }
  anomalies: Anomalie[]
  annuaire: { retrouves: number; corriges: { avant: string; apres: string }[]; introuvables: string[] }
  ecrit: { crees: number; modifies: number; erreurs: { id: string; message: string }[] }
  allerRetour: { fnctsRelues: number; actionsRelues: number; ecarts: EcartAllerRetour[] }
}

/** « Import FNC-T 2026-10-22 14h05.json » : un journal par import, jamais écrasé. */
export function nomJournal(le: Date): string {
  const p = (n: number): string => String(n).padStart(2, '0')
  return `Import FNC-T ${le.getFullYear()}-${p(le.getMonth() + 1)}-${p(le.getDate())} ${p(le.getHours())}h${p(le.getMinutes())}.json`
}
